import { useState } from "react"

/**
 * Throttles how many questions fetch their results at once: instead of letting every
 * `ScheduledSurveyQuestionResults` fire its query on mount, only the first
 * `numberOfQuestionsToFetchConcurrently` questions are marked as "being fetched"; each
 * time one finishes (`onQuestionFetched`), the next queued question takes its place.
 */
export const useDelayedQuestionsQueries = (numberOfQuestionsToFetchConcurrently: number) => {
  const [ remainingQuestionsToFetch, setRemainingQuestionsToFetch ] = useState<Set<number>>(new Set())
  const [ questionsBeingFetched, setQuestionsBeingFetched ] = useState<Set<number>>(new Set())

  const queueQuestions = (questions: number[]): void => {
    const questionsToFetch = questions.splice(0, numberOfQuestionsToFetchConcurrently)
    setRemainingQuestionsToFetch(new Set(questions))
    setQuestionsBeingFetched(new Set(questionsToFetch))
  }

  const onQuestionFetched = (fetchedQuestionId: number): void => {
    if (questionsBeingFetched.has(fetchedQuestionId)) {
      questionsBeingFetched.delete(fetchedQuestionId)

      const nextQuestionIdToFetch = remainingQuestionsToFetch.values().next().value

      if (nextQuestionIdToFetch) {
        questionsBeingFetched.add(nextQuestionIdToFetch)
        remainingQuestionsToFetch.delete(nextQuestionIdToFetch)
      }

      setRemainingQuestionsToFetch(new Set(remainingQuestionsToFetch))
      setQuestionsBeingFetched(new Set(questionsBeingFetched))
    }
  }

  return {
    onQuestionFetched,
    questionsBeingFetched,
    queueQuestions,
  }
}
